import {
  GlobalSearchResult,
  IGlobalSearchResult,
} from '@app/models/GlobalSearchResult'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { Map, Record } from 'immutable'
import { LOCATION_CHANGE } from 'connected-react-router'
import { Action, Reducer } from 'redux'
import { fork, put, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export interface IGlobalSearchInstanceState {
  readonly error?: string
  readonly isSearching: boolean
  readonly results: IGlobalSearchResult
}

export interface IGlobalSearchState {
  readonly error?: string
  readonly isSearching: boolean
  readonly results: Map<string, IGlobalSearchResult>
}

const GlobalSearchStateRecord = Record({
  error: undefined,
  isSearching: false,
  results: Map<string, GlobalSearchResult>(),
})

export class GlobalSearchState extends GlobalSearchStateRecord
  implements IGlobalSearchState {
  public static fromJS(initialState?: Partial<IGlobalSearchState>) {
    if (!initialState) {
      return new GlobalSearchState()
    }

    const { results, ...rest } = initialState

    return new GlobalSearchState({
      ...rest,
    })
  }

  public readonly error?: string
  public readonly isSearching: boolean
  public readonly results: Map<string, GlobalSearchResult>
  public readonly searchFilter: string

  constructor(props?: Partial<GlobalSearchState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  Search = 'GLOBAL_SEARCH::SEARCH',
  SearchFailure = 'GLOBAL_SEARCH::SEARCH_FAILURE',
  SearchRequest = 'GLOBAL_SEARCH::SEARCH_REQUEST',
  SearchSuccess = 'GLOBAL_SEARCH::SEARCH_SUCCESS',
  SearchUnrestricted = 'GLOBAL_SEARCH::SEARCH_UNRESTRICTED',
}

interface ISearchAction {
  id: string
  searchFilter: string
  type: ActionTypes.Search
}

interface ISearchFailureAction {
  error: string
  type: ActionTypes.SearchFailure
}

interface ISearchRequestAction {
  type: ActionTypes.SearchRequest
}

interface ISearchSuccessAction {
  id: string
  results: GlobalSearchResult
  type: ActionTypes.SearchSuccess
}

interface ISearchUnrestrictedAction {
  id: string
  searchFilter: string
  type: ActionTypes.SearchUnrestricted
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ISearchFailureAction
  | ISearchRequestAction
  | ISearchSuccessAction

// -------------------------
// ACTION CREATORS
// -------------------------

function search(id: string, searchFilter: string): ISearchAction {
  return {
    id,
    searchFilter,
    type: ActionTypes.Search,
  }
}

function searchFailure(error: string): ISearchFailureAction {
  return {
    error,
    type: ActionTypes.SearchFailure,
  }
}

function searchRequest(): ISearchRequestAction {
  return {
    type: ActionTypes.SearchRequest,
  }
}

function searchSuccess(
  id: string,
  results: GlobalSearchResult,
): ISearchSuccessAction {
  return {
    id,
    results,
    type: ActionTypes.SearchSuccess,
  }
}

function searchUnrestricted(
  id: string,
  searchFilter: string,
): ISearchUnrestrictedAction {
  return {
    id,
    searchFilter,
    type: ActionTypes.SearchUnrestricted,
  }
}

export const actionCreators = {
  search,
  searchUnrestricted,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<GlobalSearchState> = (
  state: GlobalSearchState = new GlobalSearchState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.SearchFailure:
      return state
        .set('error', action.error)
        .set('isSearching', false) as GlobalSearchState

    case ActionTypes.SearchRequest:
      return state.set('isSearching', true) as GlobalSearchState

    case ActionTypes.SearchSuccess:
      return state
        .set('isSearching', false)
        .setIn(['results', action.id], action.results) as GlobalSearchState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  if (incomingAction.type === LOCATION_CHANGE) {
    return state.set(
      'results',
      Map<string, GlobalSearchResult>(),
    ) as GlobalSearchState
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* searchSaga(action: ISearchAction) {
  try {
    yield put(searchRequest())

    if (!action.searchFilter) {
      yield put(searchSuccess(action.id, new GlobalSearchResult()))
      return
    }

    const url = `/api/Search?filter=${encodeURIComponent(action.searchFilter)}`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      yield put(searchSuccess(action.id, GlobalSearchResult.fromJS(result)))
    } else {
      yield put(searchFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(searchFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* searchUnrestrictedSaga(action: ISearchUnrestrictedAction) {
  try {
    yield put(searchRequest())

    if (!action.searchFilter) {
      yield put(searchSuccess(action.id, new GlobalSearchResult()))
      return
    }

    const url = `/api/Search/Unrestricted?filter=${encodeURIComponent(
      action.searchFilter,
    )}`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      yield put(searchSuccess(action.id, GlobalSearchResult.fromJS(result)))
    } else {
      yield put(searchFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(searchFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchSearch() {
  yield takeLatest(ActionTypes.Search, searchSaga)
}

function* watchSearchUnrestricted() {
  yield takeLatest(ActionTypes.SearchUnrestricted, searchUnrestrictedSaga)
}

export function* globalSearchRootSaga() {
  yield fork(watchSearch)
  yield fork(watchSearchUnrestricted)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState) => state.globalSearch

export const selectError = () =>
  createSelector(selectModuleState(), (mState) => mState.error)

export const selectIsSearching = () =>
  createSelector(selectModuleState(), (mState) => mState.isSearching)

export const selectResults = (id: string) =>
  createSelector(selectModuleState(), (mState) =>
    mState.results.get(id, new GlobalSearchResult()),
  )
